import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Amplify, API, graphqlOperation } from "aws-amplify";
import {
  Alert,
  Divider,
  FieldGroupIcon,
  Flex,
  SelectField,
  Text,
  TextField,
  View,
} from "@aws-amplify/ui-react";
import { Box } from "theme-ui";
import {
  Button as LumosButton,
  Card as LumosCard,
  Heading,
  Tag,
  useNotify,
} from "@loanpal/lumos__core";
import "@aws-amplify/ui-react/styles.css";
import awsExports from "../../aws-exports";
import logo from "../../images/goodleap-white-orange.svg";
import Loading from "../../utils/loading";
import NotFoundPage from "../../utils/not-found";
import Users from "../request/users";
import { getRequests, getSharedApp } from "../../graphql/queries";
import { updateRequests, updateSharedApp } from "../../graphql/mutations";
import {
  createUser as createUserApi,
  getChannels as getChannelsApi,
  getRoles as getRolesApi,
  getToken as getTokenApi,
  getUserByEmail as getUserByEmailApi,
  getUserById as getUserByIdApi,
  getUsers as getUsersApi,
  userChannels as userChannelsApi,
} from "../../utils/api";
import { Roles } from "../../utils/roles";
import { email as validateEmail } from "../../utils/formValidation";
import { phone as phone10digit, phoneDigits } from "../../utils/format";
import { generatePassword } from "../../utils/passwordGenerator";
import { serializeSharedAppStates } from "../../utils/shared-app-states";

Amplify.configure(awsExports);

const validRoles = Roles;

const initialFormState = {
  id: "",
  clientId: "",
  status: "",
  accountName: "",
  salesforce: "",
  offer: "",
  offersRequested: "",
  states: null,
  channel: "",
  category: "",
  loanAmount: 0,
  paymentAmount: 0,
  calculatorType: "",
  integratorId: "",
  redirectUrl: "",
  submittingUserFname: "",
  submittingUserLname: "",
  submittingUserEmail: "",
  submittingUserPhone: "",
  submittingUserRole: "",
  sendEmail: true,
  referenceId: "",
  contactEmail: "",
  partnerEmail: "",
  accountOwnerEmail: "",
  apiKeys: false,
  notes: "",
  requestNotes: "",
  bundleClientId: "",
  singleOfferLinks: false,
  useConnectedOrgs: false,
  organizationId: "",
  parentOrganizationClientId: "",
};

function submittingUserFromRecord(record) {
  const su = record?.submittingUser ?? {};
  return {
    submittingUserFname: su.firstName || "",
    submittingUserLname: su.lastName || "",
    submittingUserEmail: su.email || "",
    submittingUserPhone: su.phone || "",
    submittingUserRole: "",
  };
}

function requestToFormState(req) {
  return {
    ...initialFormState,
    id: req.id,
    clientId: req.clientId || "",
    status: req.status || "",
    accountName: req.accountName || "",
    salesforce: req.salesforce || "",
    offer: req.offer || "",
    offersRequested: req.offersRequested || "",
    states: req.states ?? null,
    channel: req.channel || "",
    category: req.category || "",
    loanAmount: req.loanAmount ?? 0,
    paymentAmount: req.paymentAmount ?? 0,
    calculatorType: req.calculatorType || "",
    integratorId: req.integratorId || "",
    redirectUrl: req.redirectUrl || "",
    ...submittingUserFromRecord(req),
    sendEmail: req.sendEmail ?? true,
    referenceId: req.referenceId || "",
    contactEmail: req.contactEmail || "",
    partnerEmail: req.partnerEmail || "",
    accountOwnerEmail: req.accountOwnerEmail || "",
    apiKeys: req.apiKeys || false,
    notes: req.notes || "",
    requestNotes: req.requestNotes || "",
    bundleClientId: req.bundleClientId || "",
    singleOfferLinks: req.singleOfferLinks || false,
    useConnectedOrgs: req.useConnectedOrgs || false,
    organizationId: req.organizationId || "",
    parentOrganizationClientId: req.parentOrganizationClientId || "",
  };
}

function buildRequestUpdateInput(formState) {
  return {
    id: formState.id,
    clientId: formState.clientId,
    status: formState.status,
    accountName: formState.accountName,
    salesforce: formState.salesforce,
    offer: formState.offer,
    states: serializeSharedAppStates(formState.states),
    offersRequested: formState.offersRequested,
    channel: formState.channel,
    category: formState.category,
    loanAmount: formState.loanAmount,
    paymentAmount: formState.paymentAmount,
    calculatorType: formState.calculatorType,
    integratorId: formState.integratorId,
    redirectUrl: formState.redirectUrl,
    submittingUser: {
      firstName: formState.submittingUserFname,
      lastName: formState.submittingUserLname,
      email: formState.submittingUserEmail,
      phone: formState.submittingUserPhone,
    },
    sendEmail: formState.sendEmail,
    referenceId: formState.referenceId,
    contactEmail: formState.contactEmail,
    partnerEmail: formState.partnerEmail,
    accountOwnerEmail: formState.accountOwnerEmail,
    apiKeys: formState.apiKeys,
    notes: formState.notes,
    requestNotes: formState.requestNotes,
    bundleClientId: formState.bundleClientId,
    singleOfferLinks: formState.singleOfferLinks,
    useConnectedOrgs: formState.useConnectedOrgs,
    organizationId: formState.organizationId || "",
    parentOrganizationClientId: formState.parentOrganizationClientId || "",
  };
}

const FixSubmittingUser = () => {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const sharedAppId = String(searchParams.get("sharedAppId") ?? "").trim();
  const { addSnack } = useNotify();

  const [notFound, setNotFound] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [sharedAppRecord, setSharedAppRecord] = useState(null);
  const [spinner, setSpinner] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [token, setToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [users, setUsers] = useState([]);
  const [usersItems, setUsersItems] = useState(null);
  const [roles, setRoles] = useState(null);
  const [channels, setChannels] = useState(null);
  const [channelExists, setChannelExists] = useState(false);
  const [sUser, setSuser] = useState(null);
  const [userExists, setUserExists] = useState({
    exists: true,
    status: "loading",
  });
  const [suErrorMessage, setSuErrorMessage] = useState("");
  const [submittingUserCheck, setSubmittingUserCheck] = useState(true);
  const [timer, setTimer] = useState(null);
  const [initialEmail, setInitialEmail] = useState("");

  const partnerApiReady = useMemo(
    () =>
      Boolean(formState.clientId) &&
      (formState.apiKeys || token) &&
      !tokenLoading,
    [formState.clientId, formState.apiKeys, token, tokenLoading]
  );

  const emailInUserList = useMemo(() => {
    const email = String(formState.submittingUserEmail ?? "")
      .trim()
      .toLowerCase();
    if (!email || !users?.length) return null;
    return users.some(
      (user) => String(user.email ?? "").trim().toLowerCase() === email
    );
  }, [formState.submittingUserEmail, users]);

  const setInput = useCallback((key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value ?? "" }));
  }, []);

  const loadChannels = useCallback(async (clientId, authToken) => {
    try {
      const response = await getChannelsApi(clientId, "", authToken);
      if (response) setChannels(response);
    } catch (err) {
      console.log("error getting channels", err);
    }
  }, []);

  const checkChannelExists = useCallback(
    (channelName, channelList) => {
      const list = channelList ?? channels;
      if (!list) {
        setChannelExists(false);
        return;
      }
      const channel = list.find((ch) => ch.name === channelName);
      setChannelExists(Boolean(channel));
    },
    [channels]
  );

  const getUserById = useCallback(
    async (clientId, userId, authToken, channelName) => {
      try {
        const userById = await getUserByIdApi(clientId, userId, authToken);
        if (!userById) {
          setSuser(null);
          setUserExists({ exists: false, status: "undefined" });
          setSuErrorMessage("Submitting user doesn't exist");
          return;
        }
        setSuser(userById);
        if (
          validRoles.find((role) => role === userById.role?.name) === undefined
        ) {
          setUserExists({ exists: false, status: "invalid role" });
          setSuErrorMessage(
            `User exists but ${userById.role?.name} is not a valid role`
          );
          return;
        }
        const userChannels = userById.channels ?? [];
        if (
          userChannels.find(({ name }) => name?.includes(channelName)) ===
          undefined
        ) {
          setUserExists({ exists: false, status: "channel rights" });
          setSuErrorMessage(
            `User exists but needs ${channelName} channel rights`
          );
          return;
        }
        setUserExists({ exists: true, status: "ready" });
      } catch (err) {
        console.log("error getting user by id", err);
        setUserExists({ exists: false, status: "undefined" });
        setSuErrorMessage("Could not verify submitting user");
      } finally {
        setSpinner(false);
      }
    },
    []
  );

  const getUserByEmail = useCallback(
    async (clientId, suemail, authToken, channelName) => {
      try {
        setSpinner(true);
        if (!validateEmail(suemail)) {
          setUserExists({ exists: false, status: "invalid email" });
          setSuErrorMessage("Not a valid email");
          setSpinner(false);
          return;
        }
        const response = await getUserByEmailApi(clientId, suemail, authToken);
        if (response?.data?.length > 0) {
          const userById = await getUserByIdApi(
            clientId,
            response.data[0].id,
            authToken
          );
          if (userById) {
            setSuser(userById);
            setFormState((prev) => ({
              ...prev,
              submittingUserFname: userById.firstName || prev.submittingUserFname,
              submittingUserLname: userById.lastName || prev.submittingUserLname,
              submittingUserEmail: userById.email || prev.submittingUserEmail,
              submittingUserPhone: userById.phones?.[0]?.number
                ? phone10digit(userById.phones[0].number)
                : prev.submittingUserPhone,
            }));
            if (
              validRoles.find((role) => role === userById.role?.name) ===
              undefined
            ) {
              setUserExists({ exists: false, status: "invalid role" });
              setSuErrorMessage(
                `User exists but ${userById.role?.name} is not a valid role`
              );
              setSpinner(false);
              return;
            }
            const userChannels = userById.channels ?? [];
            if (
              userChannels.find(({ name }) => name?.includes(channelName)) ===
              undefined
            ) {
              setUserExists({ exists: false, status: "channel rights" });
              setSuErrorMessage(
                `User exists but needs ${channelName} channel rights`
              );
              setSpinner(false);
              return;
            }
            setUserExists({ exists: true, status: "ready" });
          } else {
            setSuser(null);
            setUserExists({ exists: false, status: "undefined" });
            setSuErrorMessage("Submitting user doesn't exist");
          }
        } else {
          setSuser(null);
          setUserExists({ exists: false, status: "undefined" });
          setSuErrorMessage("Submitting user doesn't exist in partner org");
        }
      } catch (err) {
        console.log("error getting user by email", err);
        setUserExists({ exists: false, status: "undefined" });
        setSuErrorMessage("Error looking up submitting user");
      } finally {
        setSpinner(false);
      }
    },
    []
  );

  const loadUsers = useCallback(async (clientId, authToken) => {
    try {
      const response = await getUsersApi(clientId, authToken);
      if (response?.data?.length > 0) {
        setUsers(response.data);
        const usersArrayOptions = response.data.map((element) => ({
          name: `${element.email}`,
          value: element.id,
        }));
        setUsersItems(usersArrayOptions);
      } else {
        setUsers([]);
        setUsersItems([]);
      }
    } catch (err) {
      console.log("Error getting users", err);
      setUsers([]);
      setUsersItems([]);
    }
  }, []);

  const loadRoles = useCallback(async (clientId, authToken) => {
    try {
      const response = await getRolesApi(clientId, authToken);
      if (response?.roles?.length > 0) {
        setRoles(
          response.roles.filter((role) =>
            validRoles.find((vr) => vr === role.name)
          )
        );
      }
    } catch (err) {
      console.log("error getting roles", err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPage() {
      setSpinner(true);
      setNotFound(false);
      setSaved(false);
      try {
        const reqData = await API.graphql(
          graphqlOperation(getRequests, { id: requestId })
        );
        const req = reqData?.data?.getRequests;
        if (!req?.id) {
          if (!cancelled) setNotFound(true);
          return;
        }

        let nextForm = requestToFormState(req);
        let sharedApp = null;

        if (sharedAppId) {
          const appData = await API.graphql(
            graphqlOperation(getSharedApp, { id: sharedAppId })
          );
          sharedApp = appData?.data?.getSharedApp ?? null;
          if (
            sharedApp?.id &&
            String(sharedApp.clientId ?? "") === String(req.clientId ?? "")
          ) {
            nextForm = {
              ...nextForm,
              ...submittingUserFromRecord(sharedApp),
              channel: sharedApp.channel || nextForm.channel,
            };
          }
        }

        if (!cancelled) {
          setFormState(nextForm);
          setSharedAppRecord(sharedApp?.id ? sharedApp : null);
          setInitialEmail(nextForm.submittingUserEmail);
          setUserExists({ exists: true, status: "loading" });
        }

        if (
          req.useConnectedOrgs &&
          req.parentOrganizationClientId &&
          req.organizationId
        ) {
          setTokenLoading(true);
          setTokenError("");
          try {
            const tokenResponse = await getTokenApi(
              req.parentOrganizationClientId,
              req.organizationId
            );
            if (!cancelled) {
              setToken(tokenResponse?.token ?? null);
              if (!tokenResponse?.token) {
                setTokenError(
                  "Connected org token could not be retrieved. Partner user list is unavailable."
                );
              }
            }
          } catch (err) {
            console.log("error getting connected org token", err);
            if (!cancelled) {
              setToken(null);
              setTokenError(
                "Connected org token could not be retrieved. Partner user list is unavailable."
              );
            }
          } finally {
            if (!cancelled) setTokenLoading(false);
          }
        } else if (!req.apiKeys) {
          if (!cancelled) {
            setTokenError(
              "This request does not have API keys or connected org access yet. Submitting user cannot be verified."
            );
          }
        }
      } catch (err) {
        console.log("error loading fix submitting user page", err);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setSpinner(false);
      }
    }

    if (requestId) loadPage();
    return () => {
      cancelled = true;
    };
  }, [requestId, sharedAppId]);

  useEffect(() => {
    if (!partnerApiReady) return;
    loadChannels(formState.clientId, token);
    loadRoles(formState.clientId, token);
    loadUsers(formState.clientId, token);
  }, [
    partnerApiReady,
    formState.clientId,
    token,
    loadChannels,
    loadRoles,
    loadUsers,
  ]);

  useEffect(() => {
    if (channels && formState.channel) {
      checkChannelExists(formState.channel, channels);
    }
  }, [channels, formState.channel, checkChannelExists]);

  useEffect(() => {
    if (
      !partnerApiReady ||
      !formState.clientId ||
      !formState.submittingUserEmail ||
      !submittingUserCheck
    ) {
      return;
    }
    getUserByEmail(
      formState.clientId,
      formState.submittingUserEmail,
      token,
      formState.channel
    );
    setSubmittingUserCheck(false);
  }, [
    partnerApiReady,
    formState.clientId,
    formState.submittingUserEmail,
    formState.channel,
    submittingUserCheck,
    token,
    getUserByEmail,
  ]);

  const setInputUser = (value) => {
    const user = users.find((element) => element.id === value);
    if (user && user.email !== formState.submittingUserEmail) {
      setFormState((prev) => ({
        ...prev,
        submittingUserEmail: user.email,
        submittingUserFname: user.firstName,
        submittingUserLname: user.lastName,
        submittingUserPhone: phoneDigits(user.phoneNumber?.number ?? ""),
      }));
      getUserById(formState.clientId, value, token, formState.channel);
    }
  };

  const editUser = async () => {
    if (!channels || !formState.channel || !channelExists || !sUser) {
      addSnack({
        variant: "warning",
        message:
          "Make sure channel exists in client config and submitting user is selected.",
      });
      return;
    }
    const channel = formState.channel;
    const channelRecord = channels.find((ch) => ch.name === channel);
    if (!channelRecord) return;

    let userExistingChannels = sUser.channels?.map((ch) => ch.code) ?? [];
    const states = channelRecord.states ?? [];
    const channelsMap = states.map((st) => `${st}_${channel}`);
    const allChannels = [...userExistingChannels, ...channelsMap];

    try {
      setSpinner(true);
      const payload = JSON.stringify({ channels: allChannels });
      await userChannelsApi(formState.clientId, sUser.id, payload, token);
      await getUserByEmail(
        formState.clientId,
        formState.submittingUserEmail,
        token,
        formState.channel
      );
      addSnack({ variant: "success", message: "Channel added to user." });
    } catch (err) {
      console.log("error updating channels", err);
      addSnack({ variant: "danger", message: "Error adding channel to user." });
      setSpinner(false);
    }
  };

  const validateCreateUser = () => {
    let error = "";
    if (!formState.submittingUserEmail) error += "[Email]";
    if (!formState.submittingUserFname) error += " [First Name]";
    if (!formState.submittingUserLname) error += " [Last Name]";
    if (!formState.submittingUserRole) error += " [Role]";
    if (error !== "") {
      addSnack({
        variant: "danger",
        message: `Please enter submitting user ${error}`,
      });
      return false;
    }
    if (!channelExists) {
      addSnack({
        variant: "danger",
        message: "Offer channel is not configured for this request.",
      });
      return false;
    }
    return true;
  };

  const createUser = async () => {
    if (!validateCreateUser()) return;
    try {
      setSpinner(true);
      const payload = {
        firstName: formState.submittingUserFname,
        lastName: formState.submittingUserLname,
        password: generatePassword(12),
        email: formState.submittingUserEmail,
        mobilePhone: formState.submittingUserPhone,
        roleId: formState.submittingUserRole,
      };
      const response = await createUserApi(formState.clientId, payload, token);
      if (response?.id) {
        addSnack({ variant: "success", message: "User created successfully." });
        await loadUsers(formState.clientId, token);
        await getUserById(
          formState.clientId,
          response.id,
          token,
          formState.channel
        );
      } else {
        addSnack({ variant: "danger", message: "Error creating user." });
        setSpinner(false);
      }
    } catch (err) {
      console.log("error creating user", err);
      addSnack({ variant: "danger", message: "Error creating user." });
      setSpinner(false);
    }
  };

  const saveSubmittingUser = async () => {
    if (userExists.status !== "ready") {
      addSnack({
        variant: "danger",
        message:
          "Select or create a valid submitting user before saving.",
      });
      return;
    }
    try {
      setSaving(true);
      await API.graphql(
        graphqlOperation(updateRequests, {
          input: buildRequestUpdateInput(formState),
        })
      );

      if (sharedAppRecord?.id) {
        await API.graphql(
          graphqlOperation(updateSharedApp, {
            input: {
              id: sharedAppRecord.id,
              submittingUser: {
                firstName: formState.submittingUserFname,
                lastName: formState.submittingUserLname,
                email: formState.submittingUserEmail,
                phone: formState.submittingUserPhone,
              },
            },
          })
        );
      }

      setSaved(true);
      setInitialEmail(formState.submittingUserEmail);
      addSnack({
        variant: "success",
        message: sharedAppRecord?.id
          ? "Submitting user updated on request and GoodLink."
          : "Submitting user updated on request.",
      });
    } catch (err) {
      console.log("error saving submitting user", err);
      addSnack({ variant: "danger", message: "Error saving submitting user." });
    } finally {
      setSaving(false);
    }
  };

  if (notFound) return <NotFoundPage />;

  const statusBanner = (() => {
    if (!partnerApiReady) return null;
    if (userExists.status === "loading") {
      return (
        <Alert variation="info" isDismissible={false}>
          Checking whether the submitting user exists in the partner org…
        </Alert>
      );
    }
    if (userExists.status === "ready") {
      return (
        <Alert variation="success" isDismissible={false}>
          Submitting user is valid and ready ({formState.channel} channel).
        </Alert>
      );
    }
    if (initialEmail && emailInUserList === false) {
      return (
        <Alert variation="warning" isDismissible={false}>
          Configured submitting user{" "}
          <strong>{initialEmail}</strong> was not found in the partner org.
          Select an existing user below or create a new one.
        </Alert>
      );
    }
    return (
      <Alert variation="warning" isDismissible={false}>
        {suErrorMessage ||
          "Submitting user needs to be fixed before applications can use it reliably."}
      </Alert>
    );
  })();

  return (
    <>
      <View style={styles.heading}>
        <img src={logo} alt="GoodLeap" style={styles.logo} />
      </View>
      <Box sx={styles.container}>
        <Heading variant="h3" sx={{ marginBottom: 2 }}>
          Fix submitting user
        </Heading>
        <Text sx={styles.subTitle}>
          Request {formState.accountName ? `— ${formState.accountName}` : requestId}
          {sharedAppRecord?.id ? ` · GoodLink ${sharedAppRecord.id}` : ""}
        </Text>

        {tokenError && (
          <Alert variation="error" isDismissible={false} sx={{ marginTop: 2 }}>
            {tokenError}
          </Alert>
        )}

        {statusBanner}

        <LumosCard
          header={{ title: "Submitting user" }}
          sx={{ marginTop: 20, marginBottom: 20, overflow: "visible" }}
        >
          {!partnerApiReady && !tokenError ? (
            <Loading />
          ) : (
            <>
              {usersItems && (
                <Users
                  usersItems={usersItems}
                  setInput={setInputUser}
                  sUser={sUser}
                  allowSearch
                  viewportAwareMenu
                />
              )}

              <Flex direction="column" gap="1rem">
                <TextField
                  label="Submitting user email"
                  errorMessage={suErrorMessage}
                  hasError={!userExists.exists}
                  onChange={(event) => {
                    clearTimeout(timer);
                    setSubmittingUserCheck(false);
                    setInput("submittingUserEmail", event.target.value);
                    const newTimer = setTimeout(() => {
                      setSubmittingUserCheck(true);
                    }, 1000);
                    setTimer(newTimer);
                  }}
                  size="small"
                  value={formState.submittingUserEmail}
                  descriptiveText={
                    emailInUserList === false
                      ? "Email is not in the partner user list"
                      : emailInUserList === true
                        ? "Email found in partner user list"
                        : undefined
                  }
                  innerEndComponent={
                    formState.submittingUserEmail ? (
                      <FieldGroupIcon
                        ariaLabel="Check"
                        variation="link"
                        height="90%"
                      >
                        {userExists.status === "ready" && (
                          <i className="gg-check-o-green" />
                        )}
                      </FieldGroupIcon>
                    ) : null
                  }
                />
              </Flex>

              {!userExists.exists && userExists.status === "channel rights" && (
                <LumosButton
                  sx={{ marginTop: "10px", width: "250px" }}
                  onClick={editUser}
                >
                  Add channel to user
                </LumosButton>
              )}

              <Flex direction="row" alignItems="flex-start" marginTop="1rem">
                <TextField
                  label="First name"
                  onChange={(event) =>
                    setInput("submittingUserFname", event.target.value)
                  }
                  size="small"
                  width="50%"
                  value={formState.submittingUserFname}
                />
                <TextField
                  label="Last name"
                  onChange={(event) =>
                    setInput("submittingUserLname", event.target.value)
                  }
                  size="small"
                  width="50%"
                  value={formState.submittingUserLname}
                />
              </Flex>

              <TextField
                label="Phone"
                onChange={(event) =>
                  setInput("submittingUserPhone", event.target.value)
                }
                size="small"
                width="50%"
                descriptiveText="10 digits, no other characters"
                value={formState.submittingUserPhone}
                marginTop="1rem"
              />

              {sUser?.channels?.length > 0 && (
                <Box sx={{ marginTop: "1rem" }}>
                  <Heading variant="h6">User channels</Heading>
                  {sUser.channels.map((ch) => (
                    <span key={ch.code} style={styles.tag}>
                      <Tag>{ch.name}</Tag>
                    </span>
                  ))}
                </Box>
              )}

              <Divider orientation="horizontal" size="small" margin="1rem 0" />

              {roles && userExists.status === "undefined" && (
                <>
                  <SelectField
                    label="Role"
                    value={formState.submittingUserRole}
                    onChange={(e) =>
                      setInput("submittingUserRole", e.target.value)
                    }
                    size="small"
                    width="50%"
                  >
                    <option value="" />
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </SelectField>
                  <LumosButton sx={{ marginTop: "1rem" }} onClick={createUser}>
                    Create user
                  </LumosButton>
                </>
              )}

            </>
          )}
        </LumosCard>

        <LumosButton
          onClick={saveSubmittingUser}
          disabled={
            saving ||
            !partnerApiReady ||
            userExists.status !== "ready" ||
            saved
          }
          sx={{ marginBottom: "40px" }}
        >
          {saved ? "Saved" : saving ? "Saving…" : "Save submitting user"}
        </LumosButton>
      </Box>
      {(spinner && !formState.id) || saving ? <Loading /> : null}
    </>
  );
};

const styles = {
  logo: {
    width: 250,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: 10,
  },
  heading: {
    backgroundColor: "#003439",
  },
  container: {
    width: 960,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "20px 20px",
  },
  subTitle: {
    fontSize: "14px",
    fontWeight: "200",
  },
  tag: {
    display: "inline-block",
    marginRight: 8,
    marginBottom: 8,
  },
};

export default FixSubmittingUser;
